import { Component, inject, OnInit } from '@angular/core';
import { TimesheetsFeatureService } from '../../../features/timesheets-feature/timesheets-feature.service';
import { TimesheetLine } from '../../../domain/timesheets-domain';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { ProjectsFeatureService } from '../../../features/projects-feature/projects-feature.service';
import { TimesheetFiltersComponent } from './components/timesheet-filters/timesheet-filters.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DatePipe, DecimalPipe } from '@angular/common';
import { HighlightPipe } from '../../../utils/highlight.pipe';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SqlService } from '../../../services/sql-service/sql-service.service';
import { AuthenticationFeatureService } from '../../../features/authentication-feature/authentication-feature.service';
import { SyncToZohoDialogComponent } from '../../../dialog/sync-to-zoho-dialog/sync-to-zoho-dialog.component';

@Component({
  selector: 'app-timesheets',
  standalone: true,
  imports: [MatExpansionModule, MatButton, MatIconButton, MatIconModule, MatListModule, TimesheetFiltersComponent, MatProgressSpinnerModule, DecimalPipe, DatePipe, HighlightPipe],
  templateUrl: './timesheets.component.html',
  styleUrl: './timesheets.component.css',
})
export class TimesheetsComponent implements OnInit {
  _timesheetFeature = inject(TimesheetsFeatureService);
  _projectFeature = inject(ProjectsFeatureService);
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private _sqlService = inject(SqlService);
  private _authFeature = inject(AuthenticationFeatureService);

  isExpanded = true;
  isSyncing = false;

  constructor() {}

  ngOnInit() {
    this._timesheetFeature.fetchTimesheetLines().subscribe();
    if (!this._projectFeature.projects().length) this._projectFeature.fetchProjects().subscribe();
    if (!this._projectFeature.phases().length) this._projectFeature.fetchPhases().subscribe();
    if (!this._projectFeature.tasks().length) this._projectFeature.fetchTasks().subscribe();
  }

  generateTimesheetLinesListByDate() {
    return this._timesheetFeature.groupTimesheetLinesByDate(this._timesheetFeature.timesheetLines());
  }

  generateTimesheetLinesByWeek() {
    return this._timesheetFeature.groupTimesheetLinesByWeek(this._timesheetFeature.timesheetLines());
  }

  generateTimesheetLinesByMonth() {
    return this._timesheetFeature.groupTimesheetLinesByMonth(this._timesheetFeature.timesheetLines());
  }

  onDateFilterChanged(): void {
    const filtered = this._timesheetFeature.handleFilteringTimesheetLines(this._timesheetFeature.timesheetLines());
    if (filtered.length > 0) this.isExpanded = true;
  }

  handleOnClickAddHours(): void {
    this._timesheetFeature.handleOpeningAddingFillHourDialog();
  }

  handleOnClickEditHours(timesheetLine: TimesheetLine): void {
    this._timesheetFeature.selectedTimesheetLine.set(timesheetLine);
    this._timesheetFeature.handleOpeningEditingTimesheetLineDialog();
  }

  handleOnClickCopyHours(timesheetLine: TimesheetLine): void {
    this._timesheetFeature.handleOpeningCopyingTimesheetLineDialog(timesheetLine);
  }

  generateTimesheetLineProjectName(timesheetLine: TimesheetLine): string {
    return this._projectFeature.projects().find((p) => p.projectNo === timesheetLine.projectNo)?.projectName ?? timesheetLine.projectNo;
  }

  generateTimesheetLinePhaseName(timesheetLine: TimesheetLine): string {
    return this._projectFeature.phases().find((p) => p.phaseNo === timesheetLine.phaseNo && p.projectNo === timesheetLine.projectNo)?.phaseName ?? timesheetLine.phaseNo;
  }

  handleOnClickSyncToZoho(): void {
    const userEmail = this._authFeature.getInstance().getActiveAccount()?.username ?? '';
    this._sqlService.fetchPendingSyncEntries(userEmail).subscribe({
      next: (entries) => {
        const dialogRef = this._dialog.open(SyncToZohoDialogComponent, {
          data: { pendingCount: entries.length },
          width: '400px',
        });
        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
          if (!confirmed) return;
          this.isSyncing = true;
          this._sqlService.syncTimesheetToZoho(userEmail).subscribe({
            next: (result) => {
              this.isSyncing = false;
              if (result.failed === 0) {
                this._snackBar.open(
                  `Successfully synced ${result.synced} ${result.synced === 1 ? 'entry' : 'entries'} to Zoho Books.`,
                  undefined,
                  { duration: 5000 },
                );
              } else {
                const errorLines = [`Synced ${result.synced}, failed ${result.failed}:`, ...result.errors].join('\n');
                this._snackBar.open(errorLines, 'Close', { panelClass: 'snackbar-error' });
              }
              this._timesheetFeature.fetchTimesheetLines().subscribe();
            },
            error: (err) => {
              this.isSyncing = false;
              console.error('[Sync] zoho-timesheet error:', err);
              const message = err?.error?.error ?? err?.message ?? 'Sync to Zoho failed.';
              this._snackBar.open(message, 'Close', { panelClass: 'snackbar-error' });
            },
          });
        });
      },
    });
  }

  generateTimesheetLineTaskName(timesheetLine: TimesheetLine): string {
    return this._projectFeature.tasks().find((t) => t.taskNo === timesheetLine.taskNo)?.taskName ?? timesheetLine.taskNo;
  }

  getZohoSyncStatus(timesheetLine: TimesheetLine): boolean | null {
    const map = this._timesheetFeature.entryLinksMap();
    const key = `${timesheetLine.entryNo}_${timesheetLine.timesheetNo}`;
    if (!map.has(key)) return null;
    return map.get(key)!;
  }
}
